import classNames from "classnames";
import { IBookmarkItem, IThreadBookmark } from "helpers/bookmark";
import { getTimeAgoLabel } from "helpers/date";
import { threadPath } from "helpers/pageUrl";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import markdownToTxt from "markdown-to-txt";
import Link from "next/link";
import ThreadBookmarkActions from "./ThreadBookmarkActions";

type ThreadBookmarkProps = {
  bookmark: IBookmarkItem;
};

function ThreadBookmark({ bookmark }: ThreadBookmarkProps) {
  const [labels] = useLabels("features.bookmark-lists.bookmark-list-detail");

  const {
    interaction_count,
    is_shared,
    last_interaction_at,
    preview_text,
    thread_id,
    title,
  } = bookmark.bookmark_data as IThreadBookmark;

  // Get rid of markdown formatting, and replace newlines with dots to keep it all inline.
  const previewPlaintext = markdownToTxt(preview_text ?? "").replace(
    /\n+/g,
    " · "
  );

  return (
    <div className="border-b border-border-base pt-4 pb-1.5">
      <Link href={threadPath(thread_id, title ?? "")}>
        <h3 className="base-md">{title}</h3>
        {previewPlaintext && (
          <p className="text-fg-muted line-clamp-2 pt-3 mb-1.5">
            {previewPlaintext}
          </p>
        )}
      </Link>
      <div className="flex items-center justify-between">
        <div className="tiny-normal flex gap-1.5 text-fg-muted">
          <span>
            <span className="mr-1 text-fg-base tiny-md">
              {interaction_count}
            </span>
            {pluralize(labels["queries"], {
              QUERY_COUNT: interaction_count,
            })}
          </span>
          ·<span>{getTimeAgoLabel(last_interaction_at)}</span>·
          <span className="inline-flex items-center gap-1.5">
            <i
              className={classNames(
                "text-sm",
                is_shared ? "icon-eye" : "icon-lock"
              )}
            />
            {is_shared ? labels["shared"] : labels["private"]}
          </span>
        </div>
        <ThreadBookmarkActions bookmark={bookmark} />
      </div>
    </div>
  );
}

export default ThreadBookmark;
